import BackHeader from "@components/header/BackHeader";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { stockEditSchema } from "@libs/zod/zodVaildation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  ColorWithSize,
  ProductClassificationFields,
  ProductNameInput,
} from "@pages/register/_components";
import { Flex } from "antd";

import BasicButton from "@components/button/BasicButton";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import useModalStore from "@store/modalStore";
import { queryClient } from "@libs/reactQuery/queryClient";
import { buildClassificationPayload, createOptionKey } from "@utils/productClassification";

const StockEdit = () => {
  // 1. State
  const {
    getUserInfo,
    getProductDetail,
    updateProductStockTemp,
    postProductStockTemp,
  } = useApiRequest();
  const { modals, openModal, closeModal } = useModalStore();
  const { id, status } = useParams();
  const navigate = useNavigate();
  const [errorContext, setErrorContext] = useState("처리");
  const [errorMessage, setErrorMessage] = useState("");

  // 2. Query
  const { data: productInfo } = useQuery({
    queryKey: ["productInfo", id, status],
    queryFn: () => getProductDetail(Number(id), status),
    enabled: !!id && !!status,
  });

  // 상푸 수정
  const { mutate: updateProductStatusMutate } = useMutation({
    mutationFn: (data: any) => updateProductStockTemp(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productInfo", id, status],
      });
      navigate(`/stock-success/${id}/${status}`, {
        replace: true,
      });
    },
    onError: (error: any) => {
      openErrorModal(
        "수정",
        getMutationErrorMessage(error, "재고 수정에 실패했습니다."),
      );
    },
  });

  // 임시저장
  const { mutate: postProductStockTempMutate } = useMutation({
    mutationFn: (data: any) => postProductStockTemp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productInfo"],
      });
      navigate(-1);
    },
    onError: (error: any) => {
      openErrorModal(
        "임시저장",
        getMutationErrorMessage(error, "임시저장에 실패했습니다."),
      );
    },
  });

  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });

  const openErrorModal = (context: string, message: string) => {
    setErrorContext(context);
    setErrorMessage(message);
    openModal("errorModal");
  };

  const getMutationErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.data?.message || fallback;

  const buildFlatStockList = (stockList: any[] = []) =>
    stockList.flatMap((stock: any) =>
      (stock.size_list || []).map((size: any) => ({
        color_id: stock.color_id,
        option_key: stock.option_key || createOptionKey(),
        color_name_ko: stock.color_name_ko || stock.color_name || null,
        color_name_en: stock.color_name_en || null,
        color_name_ja: stock.color_name_ja || null,
        color_sku: stock.color_sku ?? "",
        size_sku: size.size_sku ?? "",
        color_hex_code: stock.hex_code || null,
        base_color_id: stock.base_color_id || null,
        size_id: size.size_id,
        amount: size.amount,
      })),
    );

  const buildTagPayload = (data: any) => {
    const typeTagIds = (data.tag_type_ids || []).map(Number).filter(Boolean);
    const tagIds = Array.from(
      new Set(
        [
          ...(data.tag_type_ids || []),
          ...(data.tag_style_ids || []),
          ...(data.tag_fit_ids || []),
          ...(data.tag_occasion_ids || []),
        ]
          .map(Number)
          .filter(Boolean),
      ),
    );

    return {
      type_tag_ids: typeTagIds,
      tag_ids: tagIds,
      tag_type_ids: typeTagIds,
      tag_style_ids: (data.tag_style_ids || []).map(Number).filter(Boolean),
      tag_fit_ids: (data.tag_fit_ids || []).map(Number).filter(Boolean),
      tag_occasion_ids: (data.tag_occasion_ids || []).map(Number).filter(Boolean),
    };
  };

  // 3. function

  const handleTempSubmit = async (data: any) => {
    if (!userInfo?.id) {
      openErrorModal("임시저장", "사용자 정보를 불러오지 못했습니다.");
      return;
    }

    const category_id =
      data.depth1 || productInfo?.category_depth1_id || productInfo?.category_id;
    const postStockList = buildFlatStockList(data.stock_list);

    const tempPostData = {
      product_id: productInfo?.product_id,
      user_id: userInfo.id,
      category_id: category_id,
      name_ko: data.name_ko,
      name_en: data.name_en,
      thumbnail_url: data.thumbnail_url,
      classification: buildClassificationPayload({
        audienceCategoryIds: (data.audience_category_ids || []).map(Number),
        primaryColorOptionKey: data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null,
        patterns: data.patterns || [],
      }),
      image_list: data.image_list,
      stock_list: postStockList,
      ...buildTagPayload(data),
    };

    // console.log("tempPostData", tempPostData);
    postProductStockTempMutate(tempPostData);
  };

  const handleBackEvent = (): void => {
    openModal("backModal");
  };

  // 4. useForm
  const method = useForm({
    resolver: zodResolver(stockEditSchema),

    mode: "onChange",
    reValidateMode: "onChange",

    defaultValues: {
      product_id: productInfo?.product_id || null,
      brand_id: productInfo?.brand_id || null,
      size_type_id: productInfo?.size_type_id,

      depth1: productInfo?.category_depth1_id || productInfo?.category_id || null,

      name_ko: productInfo?.product_name_ko || "",
      name_en: productInfo?.product_name_en || "",
      primary_color_id: productInfo?.primary_color_id ?? null,
      pattern: productInfo?.pattern || "",
      tag_type_ids: productInfo?.tag_type_ids || [],
      tag_style_ids: productInfo?.tag_style_ids || [],
      tag_fit_ids: productInfo?.tag_fit_ids || [],
      tag_occasion_ids: productInfo?.tag_occasion_ids || [],
      tag_color_pattern_ids: productInfo?.tag_color_pattern_ids || [],
      audience_category_ids: [] as number[],
      patterns: [] as any[],

      stock_list:
        productInfo?.color_list?.map((color: any) => ({
          color_id: color.base_color_id ? null : color.color_id,
          option_key: color.option_key || createOptionKey(),
          color_name: color.name,
          color_name_ko: color.name,
          color_name_en: color.name_en,
            color_name_ja: color.name_ja,
            color_sku: color.color_sku || "",
          base_color_id: color.base_color_id,
          hex_code: color.hex_code,
          size_list:
            color?.size_list?.map((size: any) => ({
              size_id: size.id,
              size_name: size.name,
                size_sku: size.size_sku || "",
              amount: size.amount,
            })) || [],
        })) || [],
      image_list:
        productInfo?.product_image_list.map((item: any) => item.image_url) ||
        [],

      thumbnail_url: productInfo?.product_image_list?.[0]?.image_url || "",
    },
  });

  const {
    handleSubmit,
    reset,
  } = method;

  const onSubmit = async (data: any) => {
    if (!userInfo?.id) {
      openErrorModal("수정", "사용자 정보를 불러오지 못했습니다.");
      return;
    }

    const category_id =
      data.depth1 || productInfo?.category_depth1_id || productInfo?.category_id;
    const postStockList = buildFlatStockList(data.stock_list);

    const postData = {
      product_id: productInfo?.product_id,
      user_id: userInfo.id,
      category_id: category_id,
      name_ko: data.name_ko,
      name_en: data.name_en,
      thumbnail_url: data.thumbnail_url,
      classification: buildClassificationPayload({
        audienceCategoryIds: (data.audience_category_ids || []).map(Number),
        primaryColorOptionKey: data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null,
        patterns: data.patterns || [],
      }),
      image_list: data.image_list,
      stock_list: postStockList,
      ...buildTagPayload(data),
    };

    // console.log("postData", postData);
    updateProductStatusMutate(postData);
  };

  // 5. useEffect
  useEffect(() => {
    if (productInfo) {
      reset({
        product_id: productInfo.product_id || null,
        size_type_id: productInfo.size_type_id,
        name_ko: productInfo.product_name_ko || "",
        name_en: productInfo.product_name_en || "",
        depth1: productInfo.category_depth1_id || productInfo.category_id || null,
        primary_color_id: productInfo.primary_color_id ?? null,
        pattern: productInfo.pattern || "",
        tag_type_ids: productInfo.tag_type_ids || [],
        tag_style_ids: productInfo.tag_style_ids || [],
        tag_fit_ids: productInfo.tag_fit_ids || [],
        tag_occasion_ids: productInfo.tag_occasion_ids || [],
        tag_color_pattern_ids: productInfo.tag_color_pattern_ids || [],
        audience_category_ids:
          productInfo.classification?.audience_categories?.map((item: any) => Number(item.id)) ||
          [productInfo.category_depth1_id || productInfo.category_id].filter(Boolean),
        patterns: productInfo.classification?.patterns ||
          (productInfo.pattern ? [{ tag_id: null, name_ko: productInfo.pattern }] : []),
        stock_list:
          productInfo.color_list?.map((color: any) => ({
            color_id: color.base_color_id ? null : color.color_id,
            option_key: color.option_key || createOptionKey(),
            color_name: color.name,
            color_name_ko: color.name,
            color_name_en: color.name_en,
            color_name_ja: color.name_ja,
            color_sku: color.color_sku || "",
            base_color_id: color.base_color_id,
            hex_code: color.hex_code,
            size_list:
              color.size_list?.map((size: any) => ({
                size_id: size.id,
                size_name: size.name,
                size_sku: size.size_sku || "",
                amount: size.amount,
              })) || [],
          })) || [],

        image_list: productInfo.product_image_list.map(
          (item: any) => item.image_url,
        ),
        thumbnail_url: productInfo.product_image_list?.[0]?.image_url || "",
      });
    }
  }, [productInfo, method, reset]);

  // console.log(errors);

  return (
    <FormProvider {...method}>
      <BackHeader title="상품 수정" onClick={handleBackEvent} />

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <Flex vertical gap="2rem">
          <ProductNameInput />
          <ProductClassificationFields />
          <ColorWithSize />
          <BasicButton
            title="수정"
            height="5.2rem"
            width="100%"
            padding="1.6rem 1.2rem"
            onClick={() => openModal("saveModal")}
          />
        </Flex>

        {/* 뒤로가기 모달 */}
        <BasicModal
          open={modals.backModal}
          activeName="저장하고 돌아가기"
          activeEvent={handleSubmit(handleTempSubmit)}
          closeName="그냥 돌아가기"
          closeModal={() => {
            closeModal("backModal");
            navigate("/capture-list", { replace: true });
          }}>
          <ModalContent
            mainMessage="현재까지 작성한 정보를 저장하고"
            subMessage="이전으로 돌아가시겠습니까?"
          />
        </BasicModal>

        <BasicModal
          open={modals.saveModal}
          activeName="저장"
          activeEvent={() => {
            handleSubmit(onSubmit)();
            closeModal("saveModal");
          }}
          closeName="취소"
          closeModal={() => closeModal("saveModal")}>
          <ModalContent mainMessage="현재까지 작성한 정보를 저장하시겠습니까?" />
        </BasicModal>

        <BasicModal
          open={modals.errorModal}
          activeName="확인"
          notice
          activeEvent={() => closeModal("errorModal")}
          closeModal={() => closeModal("errorModal")}>
          <ModalContent
            mainMessage={`재고 ${errorContext} 중 오류가 발생했습니다`}
            subMessage={errorMessage}
          />
        </BasicModal>
      </FormContainer>
    </FormProvider>
  );
};

export default StockEdit;

const FormContainer = styled.form({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  padding: "2rem 2rem 1rem 2rem",
});
